import type { LogoOrganization } from './logo-organization';

export enum Role {
    ADMIN = 'ADMIN',
    OWNER = 'OWNER',
    MEMBER = 'MEMBER',
}

export enum RolePermission {
    CAMERA = 'camera',
    CAMERA_MUTE = 'camera_mute',
    MICROPHONE = 'microphone',
    MICROPHONE_MUTE = 'microphone_mute',
    DESKTOP = 'desktop',
    DESKTOP_MUTE = 'desktop_mute',
    CLOSE_ROOM = 'close_room',
    RECORDING = 'recording',
    RECORDING_MUTE = 'recording_mute',
    ADMIT = 'admit',
    ADMIT_ALL = 'admit_all',
    ACCESS_LEVEL_EDIT = 'access_level_edit',
    CLOUD_RECORDING = 'cloud_recording',
    SUMMARIZATION = 'summarization',
    SUMMARIZATION_RECEIVE = 'summarization_receive',
    CALENDAR_SUMMARIZATION_RECEIVE = 'calendar_summarization_receive',
    CONFERENCE_PERMISSIONS_EDIT_MATRIX = 'conference_permissions_edit_matrix',
}

export enum PersonalPermission {
    CAMERA_MUTE = 'camera_mute',
    MICROPHONE_MUTE = 'microphone_mute',
    DESKTOP = 'desktop',
    DESKTOP_MUTE = 'desktop_mute',
    CLOSE_ROOM = 'close_room',
    ACCESS_LEVEL_EDIT = 'access_level_edit',
    RECORDING = 'recording',
    RECORDING_MUTE = 'recording_mute',
    RECORDING_BAN_ROLE = 'recording_ban_role',
    REACTION = 'reaction',
    REACTION_MUTE = 'reaction_mute',
    REACTIOM_MUTE_ALL = 'reaction_mute_all',
    REACTION_EDIT_MATRIX = 'reaction_edit_matrix',
    ADMIT = 'admit',
    ADMIT_ALL = 'admit_all',
    CLOUD_RECORDING_EDIT_MATRIX = 'cloud_recording_edit_matrix',
    CLOUD_RECORDING_BAN_ALL = 'cloud_recording_ban_all',
    CLOUD_RECORDING_BAN_ROLE = 'cloud_recording_ban_role',
    SUMMARIZATION_EDIT_MATRIX = 'summarization_edit_matrix',
    SUMMARIZATION_BAN_ALL = 'summarization_ban_all',
    SUMMARIZATION_BAN_ROLE = 'summarization_ban_role',
    SUMMARIZATION_SETTINGS = 'summarization_settings',
    CONFERENCE_PERMISSIONS_EDIT_MATRIX = 'conference_permissions_edit_matrix',
}

export interface PublicRolePermissionInfo {
    role: Role;
    allowed: RolePermission[];
}

export const reactions = ['hand'] as const;

export type ReactionType = (typeof reactions)[number];

export interface Reaction {
    reaction_type: ReactionType | null;
    timestamp: number;
    suppressible: boolean;
}

export interface AvatarPlaceholder {
    abbreviation: string;
    backgroundColor: string;
    textColor: string;
}

export type UserUid = string;

export type YandexUid = string;

export type DisplayName = string;

export type AvatarUrl = string;

export interface AvatarPlaceholderData {
    abbreviation: string;
    background_color: string;
    text_color: string;
}

export interface AvatarData {
    url?: string;
    abbreviation?: string;
}

export interface UserData {
    uid?: string;
    display_name?: string;
    avatar_url?: string;
    is_default_avatar?: boolean;
    role?: Role;
    avatar?: AvatarData;
    avatar_placeholder?: AvatarPlaceholderData;
}

export interface User {
    uid: UserUid;
    yandexuid?: YandexUid;
    login: string;
    name: string;
    avatarUrl: AvatarUrl;
    avatarPlaceholder: AvatarPlaceholder;
    accessToken?: string;
    isYandexTeamUser?: boolean;
    lang?: string;
    avatarId: string;
    isConnectAdmin: boolean;
    hasPlus: boolean;
    timezone: string;
    enabledNoB2cTariffPromo?: boolean;
    isB2b: boolean;
    hasProSubscription: boolean;
    hosted: boolean;
    displayName: DisplayName;
    isDefaultAvatar: boolean;
    broadcastAllowed: boolean;
    broadcastFeatureEnabled: boolean;
    passportUpgradeUrl?: string;
    canCreateOrganizationRestrictedConference?: boolean;
    isYandexSsoUser?: boolean;
    organizationIds?: Array<string>;
    isLegalEntity?: boolean;
    isPDD?: boolean;
    isSSO?: boolean;
    logoOrganization: LogoOrganization;
    bandwidthEffective4kCodecExp?: boolean;
}

export interface GetMeUserResponse {
    uid: UserUid;
    display_name?: DisplayName;
    avatar_url?: AvatarUrl;
    is_default_avatar?: boolean;
    avatar_placeholder: AvatarPlaceholderData;
    is_yandex_staff: boolean;
    broadcast_allowed: boolean;
    broadcast_feature_enabled: boolean;
    can_create_organization_restricted_conference: boolean;
    bandwidth_effective4k_codec_exp?: boolean;
    organization_ids?: string[];
    is_legal_entity?: boolean;
    is_pdd?: boolean;
    conferences_history_feature_enabled?: boolean;
    noise_cancellation_feature_enabled?: boolean;
}
